import React from 'react';
import classnames from 'classnames';

import {
  Banner, Slide, Slider
} from '@components/ui';
import type { SliderType } from '@components/ui';
import { CONFIG } from '@constants/config';
import { isArrayEmpty } from '@helpers/ArrayHelpers';
import { useAnalytics } from '@hooks/useAnalytics';
import { useBreakpoint } from '@hooks/useBreakpoint';

import type { IBannerImage, IHomepageBanner, IImage } from 'types';

import styles from './BannersCarouselSlot.module.scss';

interface Props {
  className?: string,
  sliderClassName?: string,
  slideClassName?: string,
  //
  banners: IHomepageBanner[],
  loading: boolean,
  //
  dataTestId: string
}

const BannersCarouselSlot = (props: Props) => {

  const {
    className,
    sliderClassName,
    slideClassName,
    //
    banners,
    loading,
    //
    dataTestId
  } = props;

  // Refs

  const swiperRef = React.useRef<SliderType>();
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout>>();

  // Hooks

  const analytics = useAnalytics();

  const isSm = useBreakpoint('sm', 'down');
  const isUpToLg = useBreakpoint('lg', 'down');

  const [navigation, setNavigation] = React.useState(false);
  const [isInitialised, setIsInitialised] = React.useState(false);

  // Effects

  React.useEffect(() => {
    setNavigation(!isUpToLg);
  }, [isUpToLg]);

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Handlers

  const onBannerClick = (index: number, name: string) => {
    if (!name) return;
    analytics.selectCarouselBanner(index + 1, name);
  };

  const onBannerEnter = (index: number, name: string) => {
    if (!name) return;
    analytics.viewCarouselBanner(index + 1, name);
  };

  // Empty

  if (isArrayEmpty(banners)) {
    return null;
  }

  // Render

  return (
    <div
      className={classnames(
        styles.root,
        { [styles.loading]: loading },
        className
      )}
      data-test-id={dataTestId}
    >
      <Slider
        //
        className={classnames(
          styles.carousel,
        )}
        sliderClassName={classnames(
          styles.slider,
          sliderClassName
        )}
        prevClassName={classnames(
          styles.carouselNavBtn,
          styles.carouselPrevBtn,
        )}
        nextClassName={classnames(
          styles.carouselNavBtn,
          styles.carouselNextBtn,
        )}
        paginationClassName={classnames(
          styles.carouselPagination,
        )}
        //
        slidesPerView={isSm ? 1.03 : 'auto'}
        slidesPerGroup={1}
        speed={350}
        centeredSlides={!isSm}
        centerInsufficientSlides={!isSm}
        watchSlidesProgress
        //
        spaceBetween={16}
        bullets={{
          position: 'center',
          bars: true,
          external: true
        }}
        navigation={navigation}
        autoplay={isUpToLg
          ? CONFIG.DURATIONS.BANNER_CAROUSEL_AUTOPLAY_RESPONSIVE
          : CONFIG.DURATIONS.BANNER_CAROUSEL_AUTOPLAY}
        loop
        //
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        onAfterInit={() => {
          // TODO: improve
          // workaround to prevent animation on initial load
          timeoutRef.current = setTimeout(() => {
            setIsInitialised(true);
          }, 400);
        }}
      >
        {
          banners?.map(({
            analyticsListId,
            analyticsListName,
            name,
            image,
            url,
            newTab,
          }, index) => {
            return (
              <Slide
                key={index}
                className={classnames(
                  styles.slide,
                  slideClassName
                )}
              >
                {
                  ({ isActive, isPrev, isNext }) => {
                    return (
                      <Banner
                        className={classnames(
                          styles.banner,
                          { [styles.active]: isActive },
                          { [styles.prev]: isPrev },
                          { [styles.next]: isNext },
                          { [styles.withTransition]: isInitialised },
                        )}
                        //
                        image={getBannerSrc(image)}
                        imageProps={{ priority: index === 0 ? true : undefined }}
                        desktopProps={{
                          width: 1435,
                          height: 557,
                        }}
                        responsiveProps={{
                          width: 828,
                          height: 424,
                        }}
                        hoverable={isActive}
                        rounded
                        loading={loading}
                        //
                        href={url}
                        newTab={newTab}
                        //
                        listProps={{
                          analyticsListId,
                          analyticsListName
                        }}
                        //
                        onClick={(e) => {
                          if (loading) {
                            e.preventDefault();
                            return;
                          }

                          if (isActive) {
                            onBannerClick(index, name);
                            return;
                          }

                          e.preventDefault();

                          if (isPrev) {
                            swiperRef.current.slidePrev();
                          }

                          if (isNext) {
                            swiperRef.current.slideNext();
                          }
                        }}
                        onEnter={() => {
                          onBannerEnter(index, name);
                        }}
                      />
                    );
                  }
                }
              </Slide>
            );
          })
        }
      </Slider>
    </div>
  );
};

// Helpers

const getBannerSrc = (image: IBannerImage): {
  desktop: IImage,
  responsive: IImage
} => {
  const {
    default: regular,
    webp,
    responsiveWebp,
    responsive
  } = image;

  return {
    desktop: {
      default: null,
      jpg: regular,
      webp
    },
    responsive: {
      default: null,
      jpg: responsive,
      webp: responsiveWebp
    }
  };
};

// Export

export default BannersCarouselSlot;
